import { useEffect, useState } from 'react';
import { api, EventDetails } from '../lib/api';
import { invitationUrl } from '../lib/invitationShare';

interface PrintableInvitation {
  id: number;
  token: string;
  name: string;
  household_name: string | null;
  max_party_size: number | null;
}

export function PrintableInvite({ invitationId }: { invitationId: number }) {
  const [invitation, setInvitation] = useState<PrintableInvitation | null>(null);
  const [event, setEvent] = useState<EventDetails | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    api<{ invitation: PrintableInvitation; event: EventDetails }>(`/admin/api/invitations/${invitationId}`)
      .then((body) => {
        if (!active) return;
        setInvitation(body.invitation);
        setEvent(body.event);
        document.title = `Print invitation — ${body.invitation.name}`;
      })
      .catch((caught) => {
        if (active) setError(caught instanceof Error ? caught.message : 'Could not load this invitation.');
      });
    return () => { active = false; };
  }, [invitationId]);

  if (error) return <main className="print-invite-shell"><div className="print-invite-error" role="alert"><h1>Invitation unavailable</h1><p>{error}</p><a href="/admin">Back to dashboard</a></div></main>;
  if (!invitation || !event) return <main className="print-invite-shell"><p className="print-invite-loading">Preparing invitation…</p></main>;

  const recipient = invitation.household_name || invitation.name;
  const url = invitationUrl(invitation.token);

  return (
    <main className="print-invite-shell">
      <div className="print-invite-tools" aria-label="Print controls">
        <a href="/admin"><i className="fa-solid fa-arrow-left" aria-hidden="true" /> Dashboard</a>
        <button type="button" onClick={() => window.print()}><i className="fa-solid fa-print" aria-hidden="true" /> Print / Save PDF</button>
      </div>
      <article className="print-invite-page">
        <div className="print-invite-border">
          <header className="print-invite-header">
            <p className="print-surprise">Shhh… it’s a surprise.</p>
            <p className="print-kicker">You’re invited to celebrate</p>
            <h1><span>Jeff’s</span><strong>70</strong><small>TH</small></h1>
            <p className="print-tagline">A birthday salute to the original Florida Man.</p>
          </header>

          <p className="print-recipient"><span>Reserved for</span>{recipient}</p>

          <section className="print-event-grid" aria-label="Event details">
            <div><span>Date</span><strong>{event.date}</strong></div>
            <div><span>Guest arrival</span><strong>{event.guestArrivalTime}</strong></div>
            <div><span>Jeff arrives</span><strong>{event.honoreeArrivalTime}</strong></div>
            <div><span>Festivities end</span><strong>{event.endTime}</strong></div>
          </section>

          <section className="print-location">
            <h2>{event.venueName}</h2>
            <p>{event.address}</p>
          </section>

          <section className="print-details">
            <p><strong>Food &amp; drink:</strong> Lunch, birthday cake, beer, and hard seltzers will be served.</p>
            <p><strong>Attire:</strong> {event.eventNotes.attire}</p>
            <p><strong>Parking:</strong> Free onsite parking is available in front of the Community Center. Nearby street parking is available if the main lot fills up.</p>
          </section>

          <aside className="print-no-gifts">No gifts, please.<br /><strong>Your presence is our present!</strong></aside>

          <section className="print-rsvp">
            <div>
              <span>RSVP by {event.rsvpDeadline}</span>
              <strong>{invitation.token}</strong>
              <small>Personal invitation code</small>
            </div>
            <div>
              <span>Reply online</span>
              <strong className="print-url">{url}</strong>
              <small>Invitation is for up to {invitation.max_party_size || 6} guests.</small>
            </div>
          </section>

          <footer>Sunday in Eustis · Seventy years · One questionable legend</footer>
        </div>
      </article>
    </main>
  );
}
